// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Every word the bench says, in English and in French (M13-26; 01 §6, 08 §3).
//
// The 2-D product is bilingual through `src/client/i18n` — a `Record` per language and a
// `replace` — and Build mode on the bench was not: the tray's tiles, the Build page's rows, the
// snap's labels, the callouts, the inspector card and the toasts were written in English in the
// modules that make them. This is the bench's own table: the same two languages, the same shape
// (a complete record per language, so a lookup cannot miss), and the same named substitution,
// because French puts a name in a different place than English does.
//
// **Kept apart from `src/client/i18n` on purpose.** The modules that make the bench's words —
// `snapView.ts`, `validationView.ts`, `inspector.ts`, `partsTray.ts`, `wristMenu.ts`, `keeping.ts`,
// `testFlip.ts` — are pure and shared with the native client case for case, and the client's
// bundle carries the whole product (payments, settings, the Builder's pages) with the storage
// behind the language choice. The bench takes a `BenchStrings` bundle as an argument, defaulting
// to English, and the host hands it the one for the language the player reads in
// (`readLanguage()` in the browser; the system language on the headset). The native client's
// `BenchStrings.cs` carries the same table, and `core/fixtures/parity/bench-strings-v1.json` —
// written by `test/xr/build/bench-strings-parity.test.ts` — holds the two to the same words.
//
// **What is not here.** The validator's own sentences (*a ball can reach (0, 0)*; *popBumper2
// overlaps flipperLeft*) are the core's, in English on every client including the 2-D Builder,
// and arrive inside `{message}`; the `[build] …` log lines are English, as every log line is.
//
// **Written for the face that draws them.** The Quest strokes these from `core/ts/art/glyphs.ts`,
// so the French uses the letters that face has — the accented lowercase added with it — and an
// ASCII apostrophe rather than the typographic one the 2-D bundle prefers, because the tiles, the
// rows and the card draw a string as it is. A capital with an accent is avoided in the wording
// (*Sortie bloquée*, not *Éjection bloquée*), because the face draws capitals at the cap height and
// an accent above one would stand above the ascender.

export type BenchLanguage = 'en' | 'fr'

export const BENCH_LANGUAGES: readonly BenchLanguage[] = ['en', 'fr']

export type BenchKey =
  // The tray's tiles (`CATEGORIES`, by id) and its one refusal.
  | 'tray.flippers'
  | 'tray.bumpers'
  | 'tray.targets'
  | 'tray.lanes'
  | 'tray.ball'
  | 'tray.fields'
  | 'tray.bankNeedsTarget'
  | 'tray.bin'
  | 'tray.binHint'
  // Why a carried part went back when the hand opened (the 1.0.113 round): too high over the
  // board, off it, or the hand lost.
  | 'carry.tooHigh'
  | 'carry.offBoard'
  | 'carry.lost'
  // The Build row and the Build page (M13-09).
  | 'menu.build'
  | 'menu.buildMenu'
  | 'menu.undo'
  | 'menu.nothingToUndo'
  | 'menu.redo'
  | 'menu.nothingToRedo'
  | 'menu.undoWhat'
  | 'menu.redoWhat'
  | 'what.parts'
  | 'what.change'
  | 'menu.reset'
  | 'menu.resetArmed'
  | 'menu.nothingToReset'
  | 'toast.resetArmed'
  | 'toast.reset'
  | 'toast.nothingToReset'
  | 'menu.symmetryOn'
  | 'menu.symmetryOff'
  | 'menu.testPlay'
  | 'menu.backToBuild'
  // The test page's rows (M13-25): the Builder's own test tools, from the wrist.
  | 'menu.freeze'
  | 'menu.run'
  | 'menu.step'
  | 'menu.stepFrozenFirst'
  | 'menu.speed'
  | 'menu.placeBall'
  | 'menu.placeBallArmed'
  | 'menu.addBall'
  | 'menu.addBallArmed'
  | 'menu.trailOn'
  | 'menu.trailOff'
  | 'menu.keep'
  | 'menu.leaveBuild'
  | 'menu.leaveArmed'
  // The snap's label (M13-13): the rule in the author's words, `{name}` the partner's id.
  | 'snap.on'
  | 'snap.inLineWith'
  | 'snap.levelWith'
  | 'snap.mirrorOf'
  | 'snap.centreLine'
  | 'snap.and'
  // The callouts' short labels (M13-17), by the geometry pass's code, and the measurement.
  | 'callout.ball-trap'
  | 'callout.device-unreachable'
  | 'callout.colliders-overlap'
  | 'callout.eject-blocked'
  | 'callout.flipper-outboard-gap'
  | 'callout.flippers-interpenetrate'
  | 'callout.layer-exit-cycle'
  | 'callout.layer-exit-dead-end'
  | 'callout.layer-hits-glass'
  | 'callout.no-ball-entry'
  | 'callout.no-centre-drain'
  | 'callout.no-drain'
  | 'callout.ramp-not-climbable'
  | 'measure.gap'
  | 'measure.overlap'
  // The inspector card's rows (M13-22): `fieldsFor(kind)`'s labels by path, the options by value.
  | 'field.length'
  | 'field.radiusPivot'
  | 'field.radiusTip'
  | 'field.restAngle'
  | 'field.sweep'
  | 'field.handedness'
  | 'field.strength'
  | 'field.returnTorque'
  | 'field.skirtRadius'
  | 'field.kickSpeed'
  | 'field.cooldownMs'
  | 'field.triggerSpeed'
  | 'field.dropSpeed'
  | 'field.autoResetMs'
  | 'field.vaneMass'
  | 'field.shaftHeight'
  | 'field.vaneDrop'
  | 'field.damping'
  | 'field.captureRadius'
  | 'field.holdMs'
  | 'field.exit.direction'
  | 'field.exit.speed'
  | 'field.captureSpeed'
  | 'field.rearmMs'
  | 'field.radius'
  | 'field.peakForce'
  | 'field.falloff'
  | 'field.passDirection'
  | 'field.armMass'
  | 'field.debounceMs'
  | 'field.travel'
  | 'field.direction'
  | 'option.left'
  | 'option.right'
  | 'option.forward'
  | 'option.reverse'
  | 'option.inverseSquare'
  | 'option.linear'
  | 'value.absent'
  | 'value.untilFired'
  // The toasts the mode itself says.
  | 'toast.notPlaced'
  | 'toast.notSet'
  | 'toast.set'
  | 'toast.symmetryOn'
  | 'toast.symmetryOff'
  | 'toast.leaveFirst'
  // The test tools' toasts (M13-25).
  | 'toast.frozen'
  | 'toast.running'
  | 'toast.stepped'
  | 'toast.speed'
  | 'toast.placeBall'
  | 'toast.ballPlaced'
  | 'toast.noBall'
  | 'toast.addBall'
  | 'toast.ballAdded'
  | 'toast.noRoom'
  | 'toast.trailOn'
  | 'toast.trailOff'
  | 'toast.pointAdded'
  | 'toast.pointLeaving'
  | 'toast.pointRemoved'
  | 'toast.twoPoints'
  | 'toast.partLeaving'
  | 'toast.partRemoved'
  | 'toast.notRemoved'
  | 'toast.openHand'
  | 'ribbon.slow'
  | 'ribbon.medium'
  | 'ribbon.fast'
  // Keep (M13-20).
  | 'keep.offline'
  | 'keep.notSignedIn'
  | 'keep.busy'
  | 'keep.nothing'
  | 'keep.keeping'
  | 'keep.keptAs'
  | 'keep.keptAgain'
  | 'keep.warningOne'
  | 'keep.warningMany'
  | 'keep.notKept'
  | 'keep.wentWrong'
  | 'keep.noId'
  | 'keep.namesTaken'
  // Test play's verdict lines (M13-10).
  | 'test.shortRoom'
  | 'test.highBar'
  | 'test.full'
  | 'test.warningOne'
  | 'test.warningMany'

export type BenchStrings = Readonly<Record<BenchKey, string>>

export const BENCH_EN: BenchStrings = {
  'tray.flippers': 'Flippers',
  'tray.bumpers': 'Bumpers & slings',
  'tray.targets': 'Targets',
  'tray.lanes': 'Lanes & gates',
  'tray.ball': 'Ball handling',
  'tray.fields': 'Fields',
  'tray.bankNeedsTarget': 'A bank is a set of drop targets. Place a drop target first.',
  'tray.bin': 'Remove',
  'tray.binHint': 'Drop a part in',
  'carry.tooHigh': 'Too high to place. Lower it toward the glass, then let go.',
  'carry.offBoard': 'Let go off the board, so nothing moved. Let go over the playfield to place it.',
  'carry.lost': 'Lost sight of your hand, so nothing moved. Try again.',

  'menu.build': 'Build',
  'menu.buildMenu': 'Build menu',
  'menu.undo': 'Undo',
  'menu.nothingToUndo': 'Nothing to undo',
  'menu.redo': 'Redo',
  'menu.nothingToRedo': 'Nothing to redo',
  'menu.undoWhat': 'Undo · {what}',
  'menu.redoWhat': 'Redo · {what}',
  'what.parts': '{n} parts',
  'what.change': 'a change',
  'menu.reset': 'Reset draft',
  'menu.resetArmed': 'Press again to reset draft',
  'menu.nothingToReset': 'Nothing to reset',
  'toast.resetArmed': 'Reset puts the machine back as it was when you entered Build. Press Reset again to do it. Undo brings your work back.',
  'toast.reset': 'Machine reset. Undo brings your work back.',
  'toast.nothingToReset': 'Nothing to reset. The machine is as it was.',
  'menu.symmetryOn': 'Symmetry on',
  'menu.symmetryOff': 'Symmetry off',
  'menu.testPlay': 'Test play',
  'menu.backToBuild': 'Back to Builder',
  'menu.freeze': 'Freeze',
  'menu.run': 'Run',
  'menu.step': 'Step 1 ms',
  'menu.stepFrozenFirst': 'Freeze to step',
  'menu.speed': 'Speed: {speed}x',
  'menu.placeBall': 'Place ball',
  'menu.placeBallArmed': 'Pinch where the ball goes',
  'menu.addBall': 'Add a ball',
  'menu.addBallArmed': 'Pinch where it goes',
  'menu.trailOn': 'Trail on',
  'menu.trailOff': 'Trail off',
  'menu.keep': 'Keep',
  'menu.leaveBuild': 'Leave Builder',
  'menu.leaveArmed': 'Press again to leave, edits lost',

  'snap.on': 'on {name}',
  'snap.inLineWith': 'in line with {name}',
  'snap.levelWith': 'level with {name}',
  'snap.mirrorOf': 'mirror of {name}',
  'snap.centreLine': 'on the centre line',
  'snap.and': ', ',

  'callout.ball-trap': 'Ball trap',
  'callout.device-unreachable': 'Unreachable',
  'callout.colliders-overlap': 'Overlapping',
  'callout.eject-blocked': 'Eject blocked',
  'callout.flipper-outboard-gap': 'Outboard gap',
  'callout.flippers-interpenetrate': 'Flippers touch',
  'callout.layer-exit-cycle': 'Exit loops',
  'callout.layer-exit-dead-end': 'Exit goes nowhere',
  'callout.layer-hits-glass': 'Hits the glass',
  'callout.no-ball-entry': 'No way in',
  'callout.no-centre-drain': 'No centre drain',
  'callout.no-drain': 'No drain',
  'callout.ramp-not-climbable': 'Cannot be shot',
  'measure.gap': '{mm} mm',
  'measure.overlap': 'overlap {mm} mm',

  'field.length': 'Length',
  'field.radiusPivot': 'Pivot radius',
  'field.radiusTip': 'Tip radius',
  'field.restAngle': 'Rest angle',
  'field.sweep': 'Sweep',
  'field.handedness': 'Hand',
  'field.strength': 'Strength',
  'field.returnTorque': 'Return spring',
  'field.skirtRadius': 'Skirt radius',
  'field.kickSpeed': 'Kick speed',
  'field.cooldownMs': 'Cooldown',
  'field.triggerSpeed': 'Trigger speed',
  'field.dropSpeed': 'Drop speed',
  'field.autoResetMs': 'Auto-reset',
  'field.vaneMass': 'Vane mass',
  'field.shaftHeight': 'Shaft height',
  'field.vaneDrop': 'Vane drop',
  'field.damping': 'Damping',
  'field.captureRadius': 'Capture radius',
  'field.holdMs': 'Hold',
  'field.exit.direction': 'Exit bearing',
  'field.exit.speed': 'Exit speed',
  'field.captureSpeed': 'Capture speed',
  'field.rearmMs': 'Re-arm',
  'field.radius': 'Reach',
  'field.peakForce': 'Peak force',
  'field.falloff': 'Falloff',
  'field.passDirection': 'Lets the ball through',
  'field.armMass': 'Arm mass',
  'field.debounceMs': 'Debounce',
  'field.travel': 'Travel',
  'field.direction': 'Bearing',
  'option.left': 'Left',
  'option.right': 'Right',
  'option.forward': 'One way',
  'option.reverse': 'The other',
  'option.inverseSquare': 'Inverse square',
  'option.linear': 'Linear',
  'value.absent': '—',
  'value.untilFired': 'Until fired',

  'toast.notPlaced': 'Not placed: {message}',
  'toast.notSet': 'Not set: {message}',
  'toast.set': '{label}: {text}',
  'toast.symmetryOn': 'Symmetry on.',
  'toast.symmetryOff': 'Symmetry off.',
  'toast.leaveFirst': 'Leave Build mode first — the bench stays where it is.',
  'toast.frozen': 'Frozen.',
  'toast.running': 'Running.',
  'toast.stepped': 'One step: +1 ms.',
  'toast.speed': 'Speed {speed}x.',
  'toast.placeBall': 'Pinch where the ball goes.',
  'toast.ballPlaced': 'Ball placed at {x}, {y} mm.',
  'toast.noBall': 'No ball in play to place.',
  'toast.addBall': 'Pinch where the new ball goes.',
  'toast.ballAdded': 'Ball added at {x}, {y} mm.',
  'toast.noRoom': 'No ball to add: none left, or no ball in play.',
  'toast.trailOn': 'Trail on.',
  'toast.trailOff': 'Trail off.',
  'toast.pointAdded': 'Point added.',
  'toast.pointLeaving': 'Let go here to take the point off.',
  'toast.pointRemoved': 'Point removed.',
  'toast.twoPoints': 'A ramp keeps at least two points.',
  'toast.partLeaving': 'Let go here to take {name} off the machine.',
  'toast.partRemoved': '{name} taken off. Undo puts it back.',
  'toast.notRemoved': 'Not taken off: {message}',
  'toast.openHand': 'Open your hand to start building.',
  'ribbon.slow': 'Slow: under {slow} m/s',
  'ribbon.medium': 'Medium: {slow} to {fast} m/s',
  'ribbon.fast': 'Fast: over {fast} m/s',

  'keep.offline': 'Keep needs the arcade — this headset cannot reach it right now.',
  'keep.notSignedIn': 'Keep needs an account - Full menu > Settings > Account',
  'keep.busy': 'Still keeping — one moment.',
  'keep.nothing': 'Nothing to keep — there is no draft on the bench.',
  'keep.keeping': 'Keeping…',
  'keep.keptAs': 'Kept as “{title}” {semver}.',
  'keep.keptAgain': 'Kept again — “{title}” {semver} has your latest.',
  'keep.warningOne': '1 warning in the Builder.',
  'keep.warningMany': '{n} warnings in the Builder.',
  'keep.notKept': 'Not kept: {message}',
  'keep.wentWrong': 'something went wrong on the way. Try again.',
  'keep.noId': 'the arcade made a machine and did not say which.',
  'keep.namesTaken': 'every name like that is taken.',

  'test.shortRoom': 'Your play space is short for a full-size cabinet — testing here at bench size.',
  'test.highBar': 'Testing here at bench size — a full-size bar would be above your eyes.',
  'test.full': 'Full size — reach for the plunger.',
  'test.warningOne': '1 warning to notice while you play.',
  'test.warningMany': '{n} warnings to notice while you play.',
}

export const BENCH_FR: BenchStrings = {
  'tray.flippers': 'Flippers',
  'tray.bumpers': 'Bumpers et slingshots',
  'tray.targets': 'Cibles',
  'tray.lanes': 'Couloirs et portes',
  'tray.ball': 'Contrôle de la bille',
  'tray.fields': 'Champs',
  'tray.bankNeedsTarget': "Une banque est un ensemble de cibles tombantes. Placez d'abord une cible tombante.",
  'tray.bin': 'Retirer',
  'tray.binHint': 'Déposez une pièce',
  'carry.tooHigh': 'Trop haut pour poser. Descendez vers la vitre, puis lâchez.',
  'carry.offBoard': "Lâché hors du plateau : rien n'a bougé. Lâchez au-dessus du plateau pour le poser.",
  'carry.lost': "Main perdue de vue : rien n'a bougé. Réessayez.",

  'menu.build': 'Construire',
  'menu.buildMenu': 'Menu Construire',
  'menu.undo': 'Annuler',
  'menu.nothingToUndo': 'Rien à annuler',
  'menu.redo': 'Rétablir',
  'menu.nothingToRedo': 'Rien à rétablir',
  'menu.undoWhat': 'Annuler · {what}',
  'menu.redoWhat': 'Rétablir · {what}',
  'what.parts': '{n} pièces',
  'what.change': 'une modification',
  'menu.reset': 'Réinitialiser',
  'menu.resetArmed': 'Appuyez encore pour réinitialiser',
  'menu.nothingToReset': 'Rien à réinitialiser',
  'toast.resetArmed': "Réinitialiser remet la machine comme à l'entrée dans l'établi. Appuyez encore sur Réinitialiser. Annuler rend votre travail.",
  'toast.reset': 'Machine réinitialisée. Annuler rend votre travail.',
  'toast.nothingToReset': 'Rien à réinitialiser. La machine est comme avant.',
  'menu.symmetryOn': 'Symétrie activée',
  'menu.symmetryOff': 'Symétrie désactivée',
  'menu.testPlay': 'Essayer',
  'menu.backToBuild': "Retour à l'établi",
  'menu.freeze': 'Figer',
  'menu.run': 'Reprendre',
  'menu.step': "Avancer d'1 ms",
  'menu.stepFrozenFirst': 'Figer pour avancer',
  'menu.speed': 'Vitesse : {speed}x',
  'menu.placeBall': 'Placer la bille',
  'menu.placeBallArmed': 'Pincez où va la bille',
  'menu.addBall': 'Ajouter une bille',
  'menu.addBallArmed': 'Pincez où la poser',
  'menu.trailOn': 'Trace activée',
  'menu.trailOff': 'Trace désactivée',
  'menu.keep': 'Conserver',
  'menu.leaveBuild': "Quitter l'établi",
  'menu.leaveArmed': 'Appuyez encore : modifications perdues',

  'snap.on': 'sur {name}',
  'snap.inLineWith': 'aligné avec {name}',
  'snap.levelWith': 'au niveau de {name}',
  'snap.mirrorOf': 'miroir de {name}',
  'snap.centreLine': 'sur la ligne centrale',
  'snap.and': ', ',

  'callout.ball-trap': 'Piège à bille',
  'callout.device-unreachable': 'Inaccessible',
  'callout.colliders-overlap': 'Chevauchement',
  'callout.eject-blocked': 'Sortie bloquée',
  'callout.flipper-outboard-gap': 'Trou au bord',
  'callout.flippers-interpenetrate': 'Flippers se touchent',
  'callout.layer-exit-cycle': 'Sortie en boucle',
  'callout.layer-exit-dead-end': 'Sortie sans issue',
  'callout.layer-hits-glass': 'Touche la vitre',
  'callout.no-ball-entry': 'Aucune entrée',
  'callout.no-centre-drain': 'Pas de drain central',
  'callout.no-drain': 'Pas de drain',
  'callout.ramp-not-climbable': 'Impossible à tirer',
  'measure.gap': '{mm} mm',
  'measure.overlap': 'chevauchement {mm} mm',

  'field.length': 'Longueur',
  'field.radiusPivot': 'Rayon au pivot',
  'field.radiusTip': 'Rayon à la pointe',
  'field.restAngle': 'Angle de repos',
  'field.sweep': 'Balayage',
  'field.handedness': 'Main',
  'field.strength': 'Force',
  'field.returnTorque': 'Ressort de rappel',
  'field.skirtRadius': 'Rayon de la jupe',
  'field.kickSpeed': 'Vitesse de renvoi',
  'field.cooldownMs': 'Temps de recharge',
  'field.triggerSpeed': 'Vitesse de déclenchement',
  'field.dropSpeed': 'Vitesse de chute',
  'field.autoResetMs': 'Remise en place auto',
  'field.vaneMass': "Masse de l'ailette",
  'field.shaftHeight': "Hauteur de l'axe",
  'field.vaneDrop': "Descente de l'ailette",
  'field.damping': 'Amortissement',
  'field.captureRadius': 'Rayon de capture',
  'field.holdMs': 'Maintien',
  'field.exit.direction': 'Cap de sortie',
  'field.exit.speed': 'Vitesse de sortie',
  'field.captureSpeed': 'Vitesse de capture',
  'field.rearmMs': 'Réarmement',
  'field.radius': 'Portée',
  'field.peakForce': 'Force maximale',
  'field.falloff': 'Atténuation',
  'field.passDirection': 'Laisse passer la bille',
  'field.armMass': 'Masse du bras',
  'field.debounceMs': 'Anti-rebond',
  'field.travel': 'Course',
  'field.direction': 'Cap',
  'option.left': 'Gauche',
  'option.right': 'Droite',
  'option.forward': 'Un sens',
  'option.reverse': "L'autre",
  'option.inverseSquare': 'Carré inverse',
  'option.linear': 'Linéaire',
  'value.absent': '—',
  'value.untilFired': 'Au tir',

  'toast.notPlaced': 'Non placé : {message}',
  'toast.notSet': 'Non réglé : {message}',
  'toast.set': '{label} : {text}',
  'toast.symmetryOn': 'Symétrie activée.',
  'toast.symmetryOff': 'Symétrie désactivée.',
  'toast.frozen': 'Figé.',
  'toast.running': 'En marche.',
  'toast.stepped': 'Un pas : +1 ms.',
  'toast.speed': 'Vitesse {speed}x.',
  'toast.placeBall': 'Pincez où va la bille.',
  'toast.ballPlaced': 'Bille placée à {x}, {y} mm.',
  'toast.noBall': 'Aucune bille en jeu à placer.',
  'toast.addBall': 'Pincez où va la nouvelle bille.',
  'toast.ballAdded': 'Bille ajoutée à {x}, {y} mm.',
  'toast.noRoom': 'Aucune bille à ajouter : plus aucune, ou aucune en jeu.',
  'toast.trailOn': 'Trace activée.',
  'toast.trailOff': 'Trace désactivée.',
  'toast.pointAdded': 'Point ajouté.',
  'toast.pointLeaving': 'Relâchez ici pour retirer le point.',
  'toast.pointRemoved': 'Point retiré.',
  'toast.twoPoints': 'Une rampe garde au moins deux points.',
  'toast.partLeaving': 'Relâchez ici pour retirer {name} de la machine.',
  'toast.partRemoved': '{name} retiré. Annuler le remet en place.',
  'toast.notRemoved': 'Non retiré : {message}',
  'toast.openHand': 'Ouvrez la main pour commencer à construire.',
  'ribbon.slow': 'Lent : moins de {slow} m/s',
  'ribbon.medium': 'Moyen : {slow} à {fast} m/s',
  'ribbon.fast': 'Rapide : plus de {fast} m/s',
  'toast.leaveFirst': "Quittez d'abord le mode Construire — l'établi reste où il est.",

  'keep.offline': "Conserver a besoin de l'arcade — ce casque ne peut pas la joindre pour l'instant.",
  'keep.notSignedIn': "Compte requis - Full menu > Settings > Account",
  'keep.busy': 'Conservation en cours — un instant.',
  'keep.nothing': "Rien à conserver — il n'y a pas de brouillon sur l'établi.",
  'keep.keeping': 'Conservation…',
  'keep.keptAs': 'Conservé sous “{title}” {semver}.',
  'keep.keptAgain': 'Conservé de nouveau — “{title}” {semver} a votre dernière version.',
  'keep.warningOne': '1 avertissement dans le Créateur.',
  'keep.warningMany': '{n} avertissements dans le Créateur.',
  'keep.notKept': 'Non conservé : {message}',
  'keep.wentWrong': 'quelque chose a mal tourné en chemin. Réessayez.',
  'keep.noId': "l'arcade a créé une machine sans dire laquelle.",
  'keep.namesTaken': 'tous les noms comme celui-là sont pris.',

  'test.shortRoom': "Votre espace de jeu est trop court pour une caisse pleine grandeur — essai ici à la taille de l'établi.",
  'test.highBar': "Essai ici à la taille de l'établi — une barre pleine grandeur serait au-dessus de vos yeux.",
  'test.full': 'Pleine grandeur — attrapez le lanceur.',
  'test.warningOne': '1 avertissement à noter en jouant.',
  'test.warningMany': '{n} avertissements à noter en jouant.',
}

export const BENCH_BUNDLES: Readonly<Record<BenchLanguage, BenchStrings>> = { en: BENCH_EN, fr: BENCH_FR }

/** Every key, in the English table's order — the order the parity fixture is written in. */
export const BENCH_KEYS: readonly BenchKey[] = Object.keys(BENCH_EN) as BenchKey[]

export function benchStringsFor(language: BenchLanguage): BenchStrings {
  return BENCH_BUNDLES[language]
}

export type BenchValues = Readonly<Record<string, string | number>>

/**
 * A sentence from the bundle, with its named values put in.
 *
 * By name and never by position (`src/client/i18n`'s rule): French puts `{name}` in a different
 * place than English does. A placeholder with no value is left standing rather than blanked,
 * because a visible `{name}` is a bug somebody reports and a gap is one nobody notices.
 */
export function benchText(strings: BenchStrings, key: BenchKey, values?: BenchValues): string {
  const template = strings[key]
  if (values === undefined) return template
  return template.replace(/\{([a-zA-Z]+)\}/g, (whole, name: string) => {
    const value = values[name]
    return value === undefined ? whole : String(value)
  })
}

/** A count's sentence: the singular line or the plural one with `{n}`. */
export function benchCount(strings: BenchStrings, one: BenchKey, many: BenchKey, n: number): string {
  return n === 1 ? benchText(strings, one, { n }) : benchText(strings, many, { n })
}
